import React from 'react'

import { FormControl, MenuItem, Select, styled } from '@mui/material'
import { AllowedConditionStatements, ObjectTypeEnum } from '../../types/SubCondition'
import { CustomFieldTypes, FIELD_STATEMENT_OPERATORS } from '../../context/CustomField/CustomFieldTypes'
import DateStatement, { DateStatementProps } from './Date/DateStatement'
import DropDownStatement from './DropDown/DropDownStatement'
import { useLocalizedTextArray } from '../../context/Localization/localizationHooks'
import { validateSubCondition } from '../../store/groupConditionsSlice'
import DollarStatement, { DollarStatementProps } from './Dollar/DollarStatement'
import NumberStatement, { NumberStatementProps } from './Number/NumberStatement'
import CurrencyStatement, { CurrencyStatementProps } from './Currency/CurrencyStatement'
import { useViewModeReadOnly } from '../../context/ViewMode/viewModeHooks'

const RootStatementContainer = styled('div')(() => ({
    display: 'flex',
    justifyContent: 'start',
    marginTop: '24px',
}))
interface StatementProps {
    fieldEId: string
    objectType: ObjectTypeEnum
    fieldType: CustomFieldTypes
    operator: string
    statement: AllowedConditionStatements
    errors: ReturnType<typeof validateSubCondition>
    onUpdateSubConditionOperator: (operator: string) => void
    onUpdateSubConditionValues: (newValues: Array<string> | Array<number>) => void
}
export const Statement = (props: StatementProps) => {
    const statementOperators = FIELD_STATEMENT_OPERATORS[props.fieldType]
    const isReadOnly = useViewModeReadOnly()
    const operatorTranslations = useLocalizedTextArray(statementOperators.map(operator => operator.translation))
    const combinedWithTranslations = statementOperators.map((operator, index) => ({ key: operator.key, translation: operatorTranslations[index] }))

    const valueError = props.errors && props.errors.type === 'value' ? props.errors : undefined

    return (
        <RootStatementContainer>
            <FormControl
                size="small"
                sx={{ m: 1, height: '40px', margin: '0px 8px' }}
            >
                <Select
                    autoWidth
                    value={props.operator}
                    onChange={(e) => props.onUpdateSubConditionOperator(e.target.value as string)}
                    disabled={isReadOnly}
                >
                    {combinedWithTranslations.map(operator => <MenuItem key={operator.key} value={operator.key}>{operator.translation}</MenuItem>)}
                </Select>
            </FormControl>

            {props.fieldType === 'Date' && (
                <DateStatement
                    operator={props.operator as DateStatementProps['operator']}
                    values={props.statement.values as string[]}
                    valueError={valueError}
                    onChangeValue={(newValues: Array<string>) => props.onUpdateSubConditionValues(newValues)}
                    disabled={isReadOnly}
                />)}
            {(props.fieldType === 'DropDown' || props.fieldType === 'Employee' || props.fieldType === 'EmployeeNoDefault' || props.fieldType === 'Radio' || props.fieldType === 'CheckBox') && (
                <DropDownStatement
                    fieldEId={props.fieldEId}
                    objectType={props.objectType}
                    values={props.statement.values as string[]}
                    valueError={valueError}
                    onChange={(newValues: Array<string>) => props.onUpdateSubConditionValues(newValues)}
                    disabled={isReadOnly}
                />)}
            {props.fieldType === 'Number' && (
                <NumberStatement 
                    operator={props.operator as NumberStatementProps['operator']}
                    objectType={props.objectType}
                    values={props.statement.values as string[]}
                    valueError={valueError}
                    onChange={(newValues: Array<string>) => props.onUpdateSubConditionValues(newValues)}
                    disabled={isReadOnly}
                />)}
            {props.fieldType === 'Amount' && (
                <DollarStatement 
                    operator={props.operator as DollarStatementProps['operator']}
                    objectType={props.objectType}
                    values={props.statement.values as string[]}
                    valueError={valueError}
                    onChange={(newValues: Array<string>) => props.onUpdateSubConditionValues(newValues)}
                    disabled={isReadOnly}
                />)}
            {props.fieldType === 'Currency' && (
                <CurrencyStatement 
                    operator={props.operator as CurrencyStatementProps['operator']}
                    objectType={props.objectType}
                    values={props.statement.values as string[]}
                    valueError={valueError}
                    onChange={(newValues: Array<string>) => props.onUpdateSubConditionValues(newValues)}
                    disabled={isReadOnly}
                />)}
        </RootStatementContainer>
    )
}

export default Statement